import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import Decimal from 'decimal.js';
import { PayoutStatusType as ExternalPayoutStatusType } from 'anchor-scout-common/types/external-api';
import { MoneyModel, timeModel } from '~/models';
import { Accounting } from '~/types/api.model';
import { InvoiceIntegrationModel } from './InvoiceIntegrationModel';
import { PayoutIntegrationModel } from './PayoutIntegrationModel';
export var ScoutPayoutFailureCode = /*#__PURE__*/ function(ScoutPayoutFailureCode) {
    ScoutPayoutFailureCode["InvalidAccountNumber"] = "invalid_account_number";
    ScoutPayoutFailureCode["BankAccountRestricted"] = "bank_account_restricted";
    ScoutPayoutFailureCode["AccountClosed"] = "account_closed";
    ScoutPayoutFailureCode["NoAccount"] = "no_account";
    ScoutPayoutFailureCode["DebitNotAuthorized"] = "debit_not_authorized";
    ScoutPayoutFailureCode["InsufficientFunds"] = "insufficient_funds";
    ScoutPayoutFailureCode["AccountFrozen"] = "account_frozen";
    ScoutPayoutFailureCode["RefusedByReceiver"] = "refused_by_receiver";
    ScoutPayoutFailureCode["MissingAuthorization"] = "missing_authorization";
    return ScoutPayoutFailureCode;
}({});
export var PayoutStatus = /*#__PURE__*/ function(PayoutStatus) {
    PayoutStatus["Paid"] = "paid";
    PayoutStatus["Failed"] = "failed";
    return PayoutStatus;
}({});
export var PayoutFailureCode = /*#__PURE__*/ function(PayoutFailureCode) {
    PayoutFailureCode["InvalidAccountNumber"] = "invalid_account_number";
    PayoutFailureCode["BankAccountRestricted"] = "bank_account_restricted";
    PayoutFailureCode["AccountClosed"] = "account_closed";
    PayoutFailureCode["NoAccount"] = "no_account";
    PayoutFailureCode["DebitNotAuthorized"] = "debit_not_authorized";
    PayoutFailureCode["InsufficientFunds"] = "insufficient_funds";
    PayoutFailureCode["AccountFrozen"] = "account_frozen";
    PayoutFailureCode["RefusedByReceiver"] = "refused_by_receiver";
    PayoutFailureCode["MissingAuthorization"] = "missing_authorization";
    return PayoutFailureCode;
}({});
export var PayoutInvoiceType = /*#__PURE__*/ function(PayoutInvoiceType) {
    PayoutInvoiceType["Payment"] = "Payment";
    PayoutInvoiceType["Refund"] = "Refund";
    PayoutInvoiceType["Dispute"] = "Dispute";
    return PayoutInvoiceType;
}({});
export class PayoutModel {
    static fromDTO(dto) {
        var _dto_integrations;
        return new PayoutModel({
            id: dto.id,
            createdAt: dto.createdAt,
            amount: MoneyModel.fromDTO(dto.amount),
            status: dto.status,
            invoices: dto.invoices.map(PayoutModel.fromPayoutInvoiceDTO),
            integrations: PayoutIntegrationModel.fromAccountingDTO((_dto_integrations = dto.integrations) !== null && _dto_integrations !== void 0 ? _dto_integrations : []),
            payoutFailure: dto.payoutFailure,
            formattedNumber: dto.formattedNumber
        });
    }
    static fromExternalDTO(dto) {
        const depositDate = new Date(dto.depositDate).getTime();
        const status = dto.status.type === ExternalPayoutStatusType.Paid ? "paid" : "failed";
        const payoutFailure = dto.status.type === ExternalPayoutStatusType.Failed ? {
            code: dto.status.failed.failureCode,
            message: dto.status.failed.message
        } : undefined;
        return new PayoutModel({
            id: dto.id,
            createdAt: depositDate,
            amount: new MoneyModel(new Decimal(dto.amount)),
            status,
            invoices: [],
            integrations: PayoutIntegrationModel.fromExternalAccountingDTO(dto.integrations),
            payoutFailure,
            formattedNumber: dto.payoutNumber
        });
    }
    static fromScoutDTO(dto) {
        var _dto_integrations;
        return new PayoutModel({
            id: dto.id,
            createdAt: dto.depositDate,
            amount: new MoneyModel(new Decimal(dto.amount)),
            status: dto.status,
            invoices: dto.invoices.map(PayoutModel.fromScoutInvoiceDTO),
            integrations: PayoutIntegrationModel.fromAccountingDTO((_dto_integrations = dto.integrations) !== null && _dto_integrations !== void 0 ? _dto_integrations : []),
            payoutFailure: dto.payoutFailure,
            formattedNumber: dto.formattedNumber
        });
    }
    static fromPayoutInvoiceDTO(dto) {
        const zeroFee = new MoneyModel(new Decimal(0));
        const fee = dto.fee ? MoneyModel.fromDTO(dto.fee) : zeroFee;
        const processingFee = MoneyModel.fromDTO(dto.processingFee);
        const customerProcessingFee = MoneyModel.fromDTO(dto.customerProcessingFee);
        const disputeFee = MoneyModel.fromDTO(dto.disputeFee);
        const totalAmount = MoneyModel.fromDTO(dto.totalAmount);
        const depositAmount = MoneyModel.fromDTO(dto.depositAmount);
        const serviceFeeRefund = MoneyModel.fromDTO(dto.serviceFeeRefund);
        return {
            id: dto.id,
            type: dto.type,
            relationshipId: dto.relationshipId,
            createdOn: timeModel.toDate(dto.createdOn),
            dueDate: timeModel.toDate(dto.dueDate),
            number: dto.number,
            formattedNumber: dto.formattedNumber,
            customer: dto.customer,
            fee,
            processingFee,
            customerProcessingFee,
            disputeFee,
            serviceFeeRefund,
            totalFees: fee.add(processingFee).add(customerProcessingFee).add(disputeFee).add(serviceFeeRefund),
            totalAmount,
            depositAmount,
            integrations: dto.integrations && InvoiceIntegrationModel.fromAccountingDTO(dto.integrations, totalAmount.isZero())
        };
    }
    static fromScoutInvoiceDTO(dto) {
        const zeroFee = new MoneyModel(new Decimal(0));
        const fee = dto.fee ? new MoneyModel(new Decimal(dto.fee)) : zeroFee;
        const processingFee = new MoneyModel(new Decimal(dto.processingFee));
        const customerProcessingFee = new MoneyModel(new Decimal(dto.customerProcessingFee));
        const disputeFee = new MoneyModel(new Decimal(dto.disputeFee));
        const totalAmount = new MoneyModel(new Decimal(dto.totalAmount));
        const depositAmount = new MoneyModel(new Decimal(dto.depositAmount));
        const serviceFeeRefund = new MoneyModel(new Decimal(dto.serviceFeeRefund));
        return {
            id: dto.id,
            type: dto.type,
            relationshipId: dto.relationshipId,
            createdOn: timeModel.toDate(dto.createdOn),
            dueDate: timeModel.toDate(dto.dueDate),
            number: dto.number,
            formattedNumber: dto.formattedNumber,
            customer: dto.customer,
            fee,
            processingFee,
            customerProcessingFee,
            disputeFee,
            serviceFeeRefund,
            totalFees: fee.add(processingFee).add(customerProcessingFee).add(disputeFee).add(serviceFeeRefund),
            totalAmount,
            depositAmount,
            integrations: dto.integrations && InvoiceIntegrationModel.fromAccountingDTO(dto.integrations, totalAmount.isZero())
        };
    }
    xeroIntegration() {
        var _this_data_integrations_find, _this_data_integrations;
        return (_this_data_integrations = this.data.integrations) === null || _this_data_integrations === void 0 ? void 0 : (_this_data_integrations_find = _this_data_integrations.find((integration)=>integration.type === Accounting.Xero)) === null || _this_data_integrations_find === void 0 ? void 0 : _this_data_integrations_find.integration;
    }
    xeroIntegrationForInvoice(id) {
        var _this_data_invoices_find_integrations, _this_data_invoices_find;
        return (_this_data_invoices_find = this.data.invoices.find((i)=>i.id === id)) === null || _this_data_invoices_find === void 0 ? void 0 : (_this_data_invoices_find_integrations = _this_data_invoices_find.integrations) === null || _this_data_invoices_find_integrations === void 0 ? void 0 : _this_data_invoices_find_integrations.find((i)=>i.type === Accounting.Xero);
    }
    qboIntegration() {
        var _this_data_integrations_find, _this_data_integrations;
        return (_this_data_integrations = this.data.integrations) === null || _this_data_integrations === void 0 ? void 0 : (_this_data_integrations_find = _this_data_integrations.find((integration)=>integration.type === Accounting.QBO)) === null || _this_data_integrations_find === void 0 ? void 0 : _this_data_integrations_find.integration;
    }
    qboIntegrationForInvoice(id) {
        var _this_data_invoices_find_integrations, _this_data_invoices_find;
        return (_this_data_invoices_find = this.data.invoices.find((i)=>i.id === id)) === null || _this_data_invoices_find === void 0 ? void 0 : (_this_data_invoices_find_integrations = _this_data_invoices_find.integrations) === null || _this_data_invoices_find_integrations === void 0 ? void 0 : _this_data_invoices_find_integrations.find((i)=>i.type === Accounting.QBO);
    }
    accountingIntegration(accounting) {
        var _this_data_integrations_find, _this_data_integrations;
        return (_this_data_integrations = this.data.integrations) === null || _this_data_integrations === void 0 ? void 0 : (_this_data_integrations_find = _this_data_integrations.find((integration)=>integration.type === accounting)) === null || _this_data_integrations_find === void 0 ? void 0 : _this_data_integrations_find.integration;
    }
    isPaid() {
        return this.data.status === "paid";
    }
    isQboSynced() {
        var _this_qboIntegration;
        return !!((_this_qboIntegration = this.qboIntegration()) === null || _this_qboIntegration === void 0 ? void 0 : _this_qboIntegration.isSynced());
    }
    isXeroSynced() {
        var _this_xeroIntegration;
        return !!((_this_xeroIntegration = this.xeroIntegration()) === null || _this_xeroIntegration === void 0 ? void 0 : _this_xeroIntegration.isSynced());
    }
    payoutFailure() {
        return this.data.payoutFailure;
    }
    amount() {
        return this.data.amount;
    }
    id() {
        return this.data.id;
    }
    createdAt() {
        return this.data.createdAt;
    }
    status() {
        return this.data.status;
    }
    formattedCreatedAt() {
        return timeModel.formatDate(this.data.createdAt);
    }
    invoices() {
        return this.data.invoices;
    }
    formattedNumber() {
        return this.data.formattedNumber;
    }
    setInvoiceIntegrations(invoiceId, integrations) {
        const updatedInvoices = this.data.invoices.map((invoice)=>{
            if (invoice.id !== invoiceId) return invoice;
            return _object_spread_props(_object_spread({}, invoice), {
                integrations: InvoiceIntegrationModel.fromAccountingDTO(integrations, invoice.totalAmount.isZero())
            });
        });
        return new PayoutModel(_object_spread_props(_object_spread({}, this.data), {
            invoices: updatedInvoices
        }));
    }
    setIntegrations(integrations) {
        const updatedIntegrations = PayoutIntegrationModel.fromAccountingDTO(integrations);
        const updatedData = _object_spread_props(_object_spread({}, this.data), {
            integrations: updatedIntegrations
        });
        return new PayoutModel(updatedData);
    }
    static fromExternalInvoiceDTO(dto) {
        const fee = new MoneyModel(new Decimal(dto.amounts.fees.serviceFee));
        const processingFee = new MoneyModel(new Decimal(dto.amounts.fees.vendorProcessingFee));
        const customerProcessingFee = new MoneyModel(new Decimal(dto.amounts.fees.customerProcessingFee));
        const disputeFee = new MoneyModel(new Decimal(dto.amounts.fees.disputeFee));
        const serviceFeeRefund = new MoneyModel(new Decimal(dto.amounts.fees.serviceFeeRefund));
        const totalAmount = new MoneyModel(new Decimal(dto.amounts.totalAmount));
        const depositAmount = new MoneyModel(new Decimal(dto.amounts.depositAmount));
        const integrations = InvoiceIntegrationModel.fromExternalAccountingDTO(dto.integrations, totalAmount.isZero());
        return {
            id: dto.invoiceId,
            type: dto.type,
            relationshipId: dto.agreement.id,
            createdOn: timeModel.toDate(new Date(dto.issueDate).getTime()),
            dueDate: timeModel.toDate(new Date(dto.dueDate).getTime()),
            formattedNumber: dto.invoiceNumber,
            customer: dto.client.business.name,
            fee,
            processingFee,
            customerProcessingFee,
            disputeFee,
            serviceFeeRefund,
            totalFees: fee.add(processingFee).add(customerProcessingFee).add(disputeFee).add(serviceFeeRefund),
            totalAmount,
            depositAmount,
            integrations
        };
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
